import moment from "moment";
import { usdFormat } from "../../../../utils";
import { TabManagementReportRow } from "./type";
import { Link } from "react-router-dom";
import { Badge } from "../../../../components/Badge";
import {ColDef} from "ag-grid-community";

type CellRenderer = (params: { value: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactFragment | React.ReactPortal | null | undefined; data: TabManagementReportRow; }) => JSX.Element;

const createCellRenderer = (permission: boolean, linkFunc: (params: { data: TabManagementReportRow }) => string): CellRenderer => {
  return (params) => (
      <>
        {permission ? (
            <Link to={linkFunc(params)}>
              <Badge>{params.value}</Badge>
            </Link>
        ) : (
            <Badge>{params.value}</Badge>
        )}
      </>
  );
};
const generateColDefs = (): ColDef<TabManagementReportRow>[] => [
  {
    headerName: 'Date',
    valueGetter: p => p.data?.transaction_time,
    pinned: 'left',
    valueFormatter: p => {
      const date = p.value ? moment(p.value) : null;
      return date ? date.format('ddd, MMM DD, YYYY h:mm a') : '';
    }
  },
  {
    headerName: 'Date_Raw',
    valueGetter: p => p.data?.transaction_time,
    hide: true,
  },
  {
    headerName: 'Username',
    valueGetter: p => p.data?.user?.username,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Order ID',
    valueGetter: p => p.data?.id,
    cellRenderer: createCellRenderer(true, (p) => `/orders/${p.data.reference_id}/${p.data?.id}`),
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Status',
    valueGetter: p => p.data?.status,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Tab Name',
    valueGetter: p => p.data?.name,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Tab Nickname',
    valueGetter: p => p.data?.nickname,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Last 4 of CC',
    valueGetter: p => p.data?.attendee?.card_on_files?.[0]?.last_four_digits ?? '',
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Reference ID',
    valueGetter: p => p.data?.reference_id,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'MX Ref ID',
    valueGetter: p => p.data?.mx_ref_id,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Subtotal',
    valueGetter: p => p.data?.subtotal,
    valueFormatter: params => usdFormat.format((params.value ?? 0)/100),
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Tax',
    valueGetter: p => p.data?.tax,
    valueFormatter: params => usdFormat.format((params.value ?? 0)/100),
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Tip',
    valueGetter: p => p.data?.tip,
    valueFormatter: params => usdFormat.format((params.value ?? 0)/100),
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Service Fee',
    valueGetter: p => p.data?.digital_surcharge,
    valueFormatter: params => usdFormat.format((params.value ?? 0)/100),
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Discount',
    valueGetter: p => p.data?.discount,
    valueFormatter: params => usdFormat.format((params.value ?? 0)/100),
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Device Identifier',
    valueGetter: p => p.data?.android_device_id,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: "Created At",
    valueGetter: (p) => p.data?.created_at,
    valueFormatter: (p) => {
      const date = p.value ? moment(p.value) : null;
      return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
    },
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Updated At",
    valueGetter: (p) => p.data?.updated_at,
    valueFormatter: (p) => {
      const date = p.value ? moment(p.value) : null;
      return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
    },
    filter: "agTextColumnFilter",
  },
  {
    headerName: 'Items',
    valueGetter: p => {
      const items = p.data?.items as any;
      if (!items) return '';

      // Open tab format: { cartItems: [{ name, quantity, price }] }
      if (items.cartItems?.length) {
        return items.cartItems
          .map((item: any) => `Name: ${item.name ?? 'N/A'} - Qty: ${item.quantity ?? 0} - Price: ${usdFormat.format((item.price ?? 0) / 100)}`)
          .join(' | ');
      }

      // Finalized format: flat array of individual items (one per unit)
      if (Array.isArray(items) && items.length) {
        const grouped = new Map<string, { name: string; price: number; qty: number }>();
        for (const item of items) {
          const key = `${item.name}_${item.price}`;
          const existing = grouped.get(key);
          if (existing) {
            existing.qty += 1;
          } else {
            grouped.set(key, { name: item.name ?? 'N/A', price: item.price ?? 0, qty: 1 });
          }
        }
        return Array.from(grouped.values())
          .map(g => `Name: ${g.name} - Qty: ${g.qty} - Price: ${usdFormat.format(g.price / 100)}`)
          .join(' | ');
      }

      return '';
    },
    filter: 'agTextColumnFilter'
  },
];

export const colDefs: ColDef<TabManagementReportRow>[] = generateColDefs();
